import type { MarketHistory } from './types';

export type HistoricalPriceHistoryPoint = {
  timestamp: string;
  bid: number | null;
  ask: number | null;
  close: number | null;
  fwd_bid: number | null;
  fwd_ask: number | null;
};

export type HistoricalPriceHistoryGap = {
  session_id: number;
  gap_start: string;
  gap_end: string;
  assets: {
    underlying_asset_id: number;
    points: HistoricalPriceHistoryPoint[];
  }[];
};

const compareTimestamps = (left: string, right: string) => {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime))
    return leftTime - rightTime;
  return left.localeCompare(right);
};

export const marketHistoryTimestampKey = (timestamp: string) => {
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? String(parsed) : timestamp;
};

/** Merge compact historical chart points without duplicating the latest send_price row. */
export const mergeHistoricalPriceHistoryGap = (
  existing: MarketHistory[],
  incoming: HistoricalPriceHistoryPoint[],
): MarketHistory[] => {
  const byTimestamp = new Map(
    existing
      .filter(point => Boolean(point.timestamp))
      .map(point => [marketHistoryTimestampKey(point.timestamp), point]),
  );

  incoming.forEach(point => {
    if (!point.timestamp) return;
    const timestampKey = marketHistoryTimestampKey(point.timestamp);
    byTimestamp.set(timestampKey, {
      ...byTimestamp.get(timestampKey),
      ...point,
    } as MarketHistory);
  });

  return Array.from(byTimestamp.values()).sort((left, right) =>
    compareTimestamps(left.timestamp, right.timestamp),
  );
};
